import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { validatePromoCode } from './api';

const CartContext = createContext(null);
const CART_KEY = 'onemaillot_cart';
const PROMO_KEY = 'onemaillot_promo_code';
const FREE_CAP = 29.99;

function readCart() {
  try {
    const data = localStorage.getItem(CART_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch { /* ignore */ }
}

/**
 * Calculate "Buy 2 Get 1 Free" promo.
 * For every 3 jerseys, the cheapest one (capped at €29.99) is free.
 * Returns { discount, freeItems: [{product_id, size, customization}] }
 */
function calculatePromo(items) {
  // Expand items by quantity into individual units
  const units = [];
  items.forEach(item => {
    for (let i = 0; i < item.quantity; i++) {
      units.push({ ...item, unitPrice: item.price });
    }
  });

  if (units.length < 3) return { discount: 0, freeCount: 0, freeItems: [] };

  // Sort by price ascending (cheapest first — those become free)
  const sorted = [...units].sort((a, b) => a.unitPrice - b.unitPrice);
  const freeCount = Math.floor(sorted.length / 3);
  let discount = 0;
  const freeItems = [];

  for (let i = 0; i < freeCount; i++) {
    const freeValue = Math.min(sorted[i].unitPrice, FREE_CAP);
    discount += freeValue;
    freeItems.push({
      product_id: sorted[i].product_id,
      size: sorted[i].size,
      customization: sorted[i].customization || '',
      savedAmount: freeValue,
    });
  }

  return { discount: Math.round(discount * 100) / 100, freeCount, freeItems };
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  // promoCode: { code: string, discount: number, finalAmount: number } | null
  const [promoCode, setPromoCode] = useState(() => {
    try {
      const raw = localStorage.getItem(PROMO_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [promoError, setPromoError] = useState('');

  const addItem = useCallback((product, size, quantity = 1, customization = '') => {
    const current = readCart();
    const key = `${product.product_id}_${size}_${customization}`;
    const existingIdx = current.findIndex(i => `${i.product_id}_${i.size}_${i.customization}` === key);
    let newItems;
    if (existingIdx >= 0) {
      newItems = current.map((i, idx) =>
        idx === existingIdx ? { ...i, quantity: i.quantity + quantity } : i
      );
    } else {
      newItems = [...current, {
        product_id: product.product_id,
        name: product.name,
        image: product.images?.[0] || '',
        price: product.price,
        size,
        quantity,
        customization,
        slug: product.slug,
      }];
    }
    writeCart(newItems);
    setItems(newItems);
  }, []);

  const removeItem = useCallback((product_id, size, customization = '') => {
    const current = readCart();
    const newItems = current.filter(i =>
      !(i.product_id === product_id && i.size === size && i.customization === customization)
    );
    writeCart(newItems);
    setItems(newItems);
  }, []);

  const updateQuantity = useCallback((product_id, size, customization, quantity) => {
    if (quantity <= 0) {
      removeItem(product_id, size, customization);
      return;
    }
    const current = readCart();
    const newItems = current.map(i =>
      i.product_id === product_id && i.size === size && i.customization === customization
        ? { ...i, quantity }
        : i
    );
    writeCart(newItems);
    setItems(newItems);
  }, [removeItem]);

  const clearCart = useCallback(() => {
    writeCart([]);
    setItems([]);
    setPromoCode(null);
    localStorage.removeItem(PROMO_KEY);
  }, []);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const promo = calculatePromo(items);
  const totalAfterAutoPromo = Math.round((subtotal - promo.discount) * 100) / 100;

  // Promo-code logic: re-validate server-side every time cart changes.
  // This keeps the discount in sync (e.g. if the user removes items below
  // `min_cart_amount`, the code becomes invalid).
  const applyPromoCode = useCallback(async (codeRaw) => {
    setPromoError('');
    const code = String(codeRaw || '').trim().toUpperCase();
    if (!code) {
      setPromoError('Saisissez un code');
      return false;
    }
    if (!items.length) {
      setPromoError('Votre panier est vide');
      return false;
    }
    try {
      const r = await validatePromoCode(code, items);
      if (!r || !r.valid) {
        setPromoError(r?.error || 'Code invalide');
        setPromoCode(null);
        localStorage.removeItem(PROMO_KEY);
        return false;
      }
      const next = { code: r.code, discount: r.discount, finalAmount: r.finalAmount };
      setPromoCode(next);
      localStorage.setItem(PROMO_KEY, JSON.stringify(next));
      return true;
    } catch (e) {
      setPromoError(e.message || 'Erreur de validation');
      return false;
    }
  }, [items]);

  const removePromoCode = useCallback(() => {
    setPromoCode(null);
    setPromoError('');
    localStorage.removeItem(PROMO_KEY);
  }, []);

  // Re-validate stored promo code when cart changes (silent — drops if invalid)
  useEffect(() => {
    if (!promoCode?.code || items.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await validatePromoCode(promoCode.code, items);
        if (cancelled) return;
        if (!r || !r.valid) {
          setPromoCode(null);
          localStorage.removeItem(PROMO_KEY);
        } else {
          const next = { code: r.code, discount: r.discount, finalAmount: r.finalAmount };
          if (next.discount !== promoCode.discount || next.finalAmount !== promoCode.finalAmount) {
            setPromoCode(next);
            localStorage.setItem(PROMO_KEY, JSON.stringify(next));
          }
        }
      } catch { /* keep current */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, subtotal]);

  // Final cap: ensure totals never go negative & promo never exceeds remaining amount
  const promoCodeDiscount = promoCode && promoCode.discount > 0
    ? Math.min(Number(promoCode.discount), Math.max(0, totalAfterAutoPromo - 0.5))
    : 0;
  const total = Math.round((totalAfterAutoPromo - promoCodeDiscount) * 100) / 100;

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQuantity, clearCart,
      subtotal, total, count, promo,
      promoCode, promoCodeDiscount, promoError, applyPromoCode, removePromoCode,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
